import React, { useRef, useState, useMemo } from 'react'
import { View, FlatList, TouchableOpacity, Image, TextInput } from 'react-native'
import { useTheme, useNavigation, useRoute, RouteProp } from '@react-navigation/native'
import {
  Screen,
  Text,
  Icon,
  KeyboardAwareView,
  BottomModalBase,
  Notes,
  BackButtonWeb,
  HiddenText,
  MessageBubbleSkeletonList,
  Skeleton,
  ConversationWithNotesSkeleton,
  TranslatableText,
} from '@components'
import getStyle from './style'
import { ConversationMessageWithFocus, ConversationParams } from './types'
import { computeLastReadSentMessageId } from './readReceipt'
import { ConversationActionsModal } from './components'
import { ConversationAiPanel } from './components/ConversationAiPanel'
import { getCommonStyles, Scope, selectImage, formatPublishedAt, showApiErrorToast } from '@utils'
import ActionItem from './components/ActionItem'
import { useScreen } from '@hooks'
import { Pin } from '../components'
import { RenderPinned } from '../components/RenderPinned'
import { MessagesStackParamList } from '@navigation'
import { EditNote } from '../../../screens/menu'
import {
  useGetConversationById,
  usePaginateMessages,
  useGetContactById,
  useGetProfile,
  Contact,
} from '@hooks'
import { useSendMessage } from '@hooks'
import type { Message } from '@hooks'
import { defaultImage } from '@assets'
import { useLanguage } from '@context'

interface ConversationProps {
  params?: ConversationParams
}

export const Conversation: React.FC<ConversationProps> = (props) => {
  const theme = useTheme()
  const navigation = useNavigation()
  const { t } = useLanguage()
  const route = useRoute<RouteProp<MessagesStackParamList, 'Conversation'>>()
  const flatListRef = useRef<FlatList>(null)

  // Check if we're in modal context
  const { conversationId } = props.params || route.params
  const { isMobile, isDesktop, isWeb } = useScreen()
  const commonStyles = getCommonStyles(theme)
  const styles = getStyle(theme)

  // API hooks
  const { data: conversation, isLoading: isLoadingConversation } =
    useGetConversationById(conversationId)
  const { data: currentUser } = useGetProfile()
  const { data: contact, isLoading: isLoadingContact } = useGetContactById(
    conversation?.participants?.find((p) => p.id !== currentUser?.accountId)?.id || '',
  )
  const {
    flatData: apiMessages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchMessages,
  } = usePaginateMessages(conversationId)
  const sendMessageMutation = useSendMessage()

  const [messageText, setMessageText] = useState('')
  const [showActionsModal, setShowActionsModal] = useState(false)
  const [image, setImage] = useState<string>()
  const [height, setHeight] = useState(40)
  const [messageId, setMessageId] = useState<string>()
  const [noteId, setNoteId] = useState<string>()
  const [isNoteShared, setIsNoteShared] = useState<boolean>(false)
  const [focusedMessageId, setFocusedMessageId] = useState<string>()
  const [showAiPanel, setShowAiPanel] = useState(false)

  // Transform API messages to component format
  const messages: ConversationMessageWithFocus[] = useMemo(() => {
    if (!apiMessages || !currentUser) return []

    return apiMessages.map((msg: Message) => {
      const isSent = msg.author.id === currentUser.accountId
      return {
        id: msg.id,
        text: msg.content,
        image: msg.imageUrl ?? undefined,
        timestamp: formatPublishedAt(msg.createdAt),
        isSent,
        senderId: msg.author.id,
        status: 'sent' as const,
        isPinned: false, // TODO: Add pin support from API
        isFocused: msg.id === focusedMessageId,
      }
    })
  }, [apiMessages, currentUser, focusedMessageId])

  // Read receipt: the id of the most recent message I sent that the other
  // person has already read (their last-read time is at/after it). "Vu" is
  // shown only under that one message, WhatsApp-style.
  const lastReadSentMessageId = useMemo(
    () =>
      computeLastReadSentMessageId(
        apiMessages,
        currentUser?.accountId,
        conversation?.othersLastReadAt,
      ),
    [apiMessages, currentUser, conversation?.othersLastReadAt],
  )

  // Get scope from conversation or use type from params
  const scope = conversation?.scope || Scope.PRIVATE

  const pinnedMessages = messages.filter((m) => m.isPinned)

  // Couleurs selon le type de contact
  const getContactColors = () => {
    switch (scope) {
      case Scope.PRIVATE:
        return {
          primary: theme.colors.green,
          light: theme.colors.green + '20',
        }
      case Scope.PROFESSIONAL:
        return {
          primary: theme.colors.primary,
          light: theme.colors.primary + '20',
        }
      /*case Scope.OTHERS:
        return {
          primary: theme.colors.greyBlue,
          light: theme.colors.greyBlue + '20'
        }*/
      default:
        return {
          primary: theme.colors.primary,
          light: theme.colors.primary + '20',
        }
    }
  }

  const colors = getContactColors()

  const renderMessage = ({ item }: { item: ConversationMessageWithFocus }) => (
    <TouchableOpacity style={styles.messageContainer} onLongPress={() => setMessageId(item.id)}>
      {item.isSent ? (
        // Message envoyé
        <View style={styles.sentMessageWrapper}>
          <View style={styles.receivedMessageContainer}>
            {item.isPinned && <Pin isFocused={item.isFocused} color={colors.primary} />}
            <View style={[styles.sentMessage, { backgroundColor: colors.primary }]}>
              {!!item.image && <Image source={{ uri: item.image }} style={styles.messageImage} />}
              {!!item.text && (
                <TranslatableText text={item.text} type="body" textStyle={styles.sentMessageText} />
              )}
            </View>
          </View>
          <View style={styles.sentMessageFooter}>
            <Text type="bodyExtraSmall" style={styles.sentMessageTime}>
              {item.timestamp}
            </Text>
            {item.id === lastReadSentMessageId && (
              <View style={styles.readReceipt}>
                <Icon name="check" size={12} color={colors.primary} />
                <Text type="bodyExtraSmall" style={[styles.sentMessageTime, { color: colors.primary }]}>
                  {t('messaging.seen')}
                </Text>
              </View>
            )}
          </View>
        </View>
      ) : (
        // Message reçu avec avatar - structure comme stepTwo.tsx
        <View style={styles.receivedMessageWrapper}>
          <View style={styles.receivedMessageContainer}>
            <View style={styles.receivedMessage}>
              {!!item.image && (
                <Image source={{ uri: item.image }} style={styles.messageImage} />
              )}
              {!!item.text && (
                <TranslatableText
                  text={item.text}
                  type="body"
                  textStyle={styles.receivedMessageText}
                />
              )}
            </View>
            {item.isPinned && <Pin isFocused={item.isFocused} color={colors.primary} />}
          </View>
          <View style={styles.messageFooter}>
            <Image
              source={contact?.avatarUrl ? { uri: contact.avatarUrl } : defaultImage}
              style={styles.messageAvatar}
            />
            <Text type="bodyExtraSmall" style={styles.receivedMessageTime}>
              {item.timestamp}
            </Text>
          </View>
        </View>
      )}
    </TouchableOpacity>
  )

  const goBack = () => {
    navigation.goBack()
  }

  const goToProfile = (profileId: string) => {
    ;(navigation as any).navigate('Profile', {
      screen: 'DetailsGhostProfile',
      params: { profileId },
    })
  }

  const sendMessage = async () => {
    const content = messageText.trim()
    const currentImage = image

    // A message needs text or an image; nothing to send otherwise.
    if ((!content && !currentImage) || !conversationId) return

    // Clear input immediately
    setMessageText('')
    setImage(undefined)

    try {
      await sendMessageMutation.mutateAsync({
        conversationId,
        content,
        imageUri: currentImage,
      })
      refetchMessages()
      // Messages will be refetched automatically via query invalidation
    } catch (error) {
      // Restore message text + image on error
      setMessageText(content)
      if (currentImage) setImage(currentImage)
      showApiErrorToast(t, error)
    }
  }

  const onAddImage = () => {
    selectImage(
      (uri) => {
        setImage(uri)
      },
      {
        title: t('photoAlert.title'),
        message: t('photoAlert.message'),
        cancel: t('common.cancel'),
        gallery: t('categories.gallery'),
        camera: t('categories.camera'),
      },
    )
  }

  const scrollToMessage = (id: string) => {
    const index = messages.findIndex((item) => item.id === id)
    if (index !== -1 && flatListRef.current) {
      setFocusedMessageId(id)
      flatListRef.current.scrollToIndex({ index, animated: true })
    }
  }

  const onAddNotePress = (isShared?: boolean) => {
    setNoteId('')
    if (isShared !== undefined) setIsNoteShared(isShared)
  }

  const handleNotePress = (noteId: string) => {
    setNoteId(noteId)
  }

  if (isLoadingConversation || isLoadingContact || !conversation || !contact) {
    if (isDesktop) {
      return (
        <Screen>
          <ConversationWithNotesSkeleton />
        </Screen>
      )
    }
    return (
      <Screen>
        <View style={commonStyles.flex}>
          <View style={styles.customHeader}>
            {isMobile && (
              <View style={styles.backButton}>
                <Skeleton width={24} height={24} borderRadius={12} />
              </View>
            )}
            <View style={styles.headerContent}>
              <View style={styles.headerLeft}>
                <Skeleton width={40} height={40} borderRadius={20} />
                <View style={[styles.onlineIndicator, { backgroundColor: theme.colors.grey }]} />
              </View>
              <View style={styles.headerInfo}>
                <Skeleton
                  width="60%"
                  height={16}
                  borderRadius={4}
                  style={{ marginBottom: theme.spacing.xs }}
                />
                <Skeleton width="40%" height={12} borderRadius={4} />
              </View>
            </View>
          </View>
          <MessageBubbleSkeletonList count={6} />
          <View style={styles.inputContainer}>
            <Skeleton width="80%" height={20} borderRadius={10} />
            <View style={styles.toolbarRow}>
              <View style={styles.toolbarIcons}>
                <Skeleton width={40} height={40} borderRadius={20} />
                <Skeleton width={40} height={40} borderRadius={20} />
              </View>
              <Skeleton width={44} height={44} borderRadius={22} />
            </View>
          </View>
        </View>
      </Screen>
    )
  }

  return (
    <Screen>
      <View
        style={
          isDesktop
            ? [commonStyles.rowNoGap, commonStyles.alignStart, commonStyles.fullHeight]
            : commonStyles.flex
        }
      >
        <KeyboardAwareView isFullScreen offsetTopInset>
          <View style={commonStyles.flex}>
            {!isMobile && route.params && <BackButtonWeb />}
            {/* Header personnalisé */}
            <View style={[styles.customHeader, { borderBottomColor: colors.primary }]}>
              {isMobile && (
                <TouchableOpacity style={styles.backButton} onPress={goBack}>
                  <Icon name="arrowBack" size={24} color={theme.colors.secondary} />
                </TouchableOpacity>
              )}

              <TouchableOpacity
                style={styles.headerContent}
                onPress={() => goToProfile(contact?.id)}
              >
                <View style={styles.headerLeft}>
                  <Image
                    source={contact.avatarUrl ? { uri: contact.avatarUrl } : defaultImage}
                    style={styles.headerAvatar}
                  />
                  <View style={[styles.onlineIndicator, { backgroundColor: colors.primary }]} />
                </View>

                <View style={styles.headerInfo}>
                  <Text type="bodyBold" style={styles.headerName}>
                    {contact?.fullName || 'Unknown'}
                  </Text>
                  <Text type="bodyExtraSmall" style={styles.headerStatus}></Text>
                </View>
              </TouchableOpacity>

              {/*<View style={styles.headerActions}>
              <TouchableOpacity style={styles.headerAction} onPress={() => {
                (navigation as any).navigate('Menu', {
                  screen: 'Notes', 
                  params: {
                    screen: 'NotesList',
                    params: {
                      profileId: contact.id
                    }
                  }
                })
              }}>
                <Icon name="note" size={20} color={theme.colors.secondary} />
              </TouchableOpacity>
              <TouchableOpacity 
                style={styles.headerAction}
                onPress={() => setShowActionsModal(true)}
              >
                <Icon name="moreVertical" size={20} color={theme.colors.secondary} />
              </TouchableOpacity>
            </View>*/}
            </View>

            {pinnedMessages.length > 0 && (
              <RenderPinned
                pinnedMessages={pinnedMessages}
                color={colors.light}
                onPress={scrollToMessage}
              />
            )}
            {/* Liste des messages */}
            <FlatList
              ref={flatListRef}
              data={messages}
              renderItem={renderMessage}
              keyExtractor={(item) => item.id}
              style={styles.messagesList}
              contentContainerStyle={styles.messagesContent}
              showsVerticalScrollIndicator={false}
              inverted
              onEndReached={() => {
                if (hasNextPage && !isFetchingNextPage) {
                  fetchNextPage()
                }
              }}
              onEndReachedThreshold={0.5}
            />

            {/* AI Panel */}
            {showAiPanel && (
              <ConversationAiPanel
                conversationId={conversationId}
                onSelectSuggestion={(text) => {
                  setMessageText(text)
                  setShowAiPanel(false)
                }}
                onClose={() => setShowAiPanel(false)}
                accentColor={colors.primary}
              />
            )}

            {/* Input de saisie */}
            <View style={styles.inputContainer}>
              <HiddenText text={messageText} setHeight={setHeight} />

              <TextInput
                style={[styles.textInput, isWeb && { height }]}
                value={messageText}
                onChangeText={setMessageText}
                placeholder={t('messaging.messagePlaceholder')}
                placeholderTextColor={theme.colors.greyBlue}
                multiline
              />

              {!!image && (
                <View style={styles.imagePreviewWrapper}>
                  <Image source={{ uri: image }} style={styles.imagePreview} />
                  <TouchableOpacity
                    style={styles.removeImageIcon}
                    onPress={() => setImage(undefined)}
                  >
                    <Icon name="cross" size={14} color={theme.colors.white} />
                  </TouchableOpacity>
                </View>
              )}

              <View style={styles.toolbarRow}>
                <View style={styles.toolbarIcons}>
                  <TouchableOpacity style={styles.iconButton} onPress={onAddImage}>
                    <Icon name="picture" size={22} color={theme.colors.greyBlue} />
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={styles.iconButton}
                    onPress={() => setShowAiPanel(!showAiPanel)}
                  >
                    <Icon
                      name="star"
                      size={22}
                      color={showAiPanel ? colors.primary : theme.colors.greyBlue}
                    />
                  </TouchableOpacity>
                </View>

                <TouchableOpacity
                  style={[
                    styles.sendButton,
                    { backgroundColor: colors.primary + '1A', borderColor: colors.primary },
                  ]}
                  onPress={sendMessage}
                >
                  <Icon name="send" size={20} color={colors.primary} />
                </TouchableOpacity>
              </View>
            </View>

            {/* Modal d'actions */}
            <ConversationActionsModal
              visible={showActionsModal}
              onClose={() => setShowActionsModal(false)}
              contactName={contact.fullName || 'Unknown'}
              onPinnedMessages={() => console.log(t('messagesScreen.pinnedMessages'))}
              onNotes={() => {
                ;(navigation as any).navigate('Menu', {
                  screen: 'Notes',
                  params: {
                    screen: 'NotesList',
                    params: {
                      profileId: contact.id,
                    },
                  },
                })
              }}
              onMedia={() => {
                ;(navigation as any).navigate('Profile', {
                  screen: 'ProfileMemories',
                  params: {
                    screen: 'MyMemories',
                  },
                })
              }}
              onBlock={() => console.log('Bloquer le contact')}
              onReport={() => console.log('Signaler le contact')}
            />
            <BottomModalBase
              isVisible={!!messageId}
              setIsVisible={() => setMessageId(undefined)}
              hasCloseButton
              largePaddingTop={false}
            >
              <View style={styles.actionsContainer}>
                <ActionItem
                  action={{
                    label: messages?.find((m) => m.id === messageId)?.isPinned
                      ? 'Désépingler'
                      : 'Épingler',
                    icon: 'pin',
                    onPress: () => {
                      // TODO: Implement pin/unpin API call
                      console.log('Pin/unpin message:', messageId)
                      setMessageId(undefined)
                    },
                  }}
                />
              </View>
            </BottomModalBase>
          </View>
        </KeyboardAwareView>
        {isDesktop && (
          <View style={[commonStyles.flex350, commonStyles.fullHeight]}>
            {noteId === undefined && (
              <Notes
                profileId={contact?.id}
                onNotePress={handleNotePress}
                onAddPress={onAddNotePress}
              />
            )}
            {noteId !== undefined && (
              <EditNote
                noteId={noteId}
                isShared={isNoteShared}
                isCreationStack
                onBack={() => setNoteId(undefined)}
                contactId={
                  conversation?.participants?.find((p) => p.id !== currentUser?.accountId)?.id
                }
                onComplete={() => setNoteId(undefined)}
              />
            )}
          </View>
        )}
      </View>
    </Screen>
  )
}
